/**
 * Pin artwork rules shared by every surface that draws a pin — the live map's
 * sprites and the server-rendered print. They live here, not in `MapPin`, so the
 * print payload builder can apply the very same recolour and anchor the screen
 * does: a pin that is red and tip-down on the map must be red and tip-down on
 * the paper, and two copies of either rule would drift.
 */

/** `hex` moved `amount` of the way towards white. */
function lighten(hex: string, amount: number): string {
  const c = hex.replace('#', '')
  if (c.length !== 6) return hex
  const r = parseInt(c.slice(0, 2), 16)
  const g = parseInt(c.slice(2, 4), 16)
  const b = parseInt(c.slice(4, 6), 16)
  const mix = (v: number) => Math.round(v + (255 - v) * amount)
  return `#${[mix(r), mix(g), mix(b)].map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

/**
 * The drawing repainted in `color`: every baked fill except white and black
 * takes the colour, and the lightest of them — when there is more than one —
 * takes a lightened accent of it, which is how a two-tone pin keeps its two
 * tones under a user's colour.
 */
export function recolourPinSvg(svgText: string, color: string): string {
  const fills = Array.from(new Set(svgText.match(/fill="#[A-Fa-f0-9]{6}"/g) ?? []))
    .filter((f) => {
      const hex = f.slice(7, -1).toLowerCase()
      return hex !== 'ffffff' && hex !== '000000'
    })

  const ranked = fills
    .map((fill) => {
      const hex = fill.slice(7, -1)
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return { fill, luma: 0.299 * r + 0.587 * g + 0.114 * b }
    })
    .sort((a, b) => a.luma - b.luma)

  const primary = color
  const accent = lighten(color, 0.45)

  let out = svgText
  ranked.forEach((r, i) => {
    const replacement = i === ranked.length - 1 && ranked.length > 1 ? accent : primary
    out = out.split(r.fill).join(`fill="${replacement}"`)
  })
  return out
}

/**
 * Every colour the drawing itself is painted in, as lowercase `#rrggbb`.
 * White is left out: it is the glyph's cut-out on every pin, never a colour
 * the type is known by.
 */
export function bakedPinColours(svgText: string): Set<string> {
  const out = new Set<string>()
  for (const [, value] of svgText.matchAll(/fill="(#[A-Fa-f0-9]{6}|black)"/g)) {
    const hex = value === 'black' ? '#000000' : value.toLowerCase()
    if (hex !== '#ffffff') out.add(hex)
  }
  return out
}

/**
 * The colour to repaint a pin in, or undefined when it draws as designed.
 *
 * `designText` is the type's WITH-background drawing, whichever variant is
 * drawn. A stored colour that is one of its baked colours is not an override —
 * it is the pin's own appearance written down, which is what the app stores on
 * every pin it creates — so it draws exactly like a pin with no colour at all.
 * Recolouring it would replace the designed secondary with a computed tint:
 * bait would go from saturated magenta to washed pink, and a team map would
 * show the same type in two accents depending on which app created the pin.
 *
 * The comparison is against the with-background drawing because the stored
 * colour comes from that one table: the bare `eggs` icon is painted in what is
 * the SECONDARY of the backgrounded pin, so matching against the bare drawing
 * would miss it. Any baked colour counts, not only the darkest, because
 * `target`'s designed primary is the lighter of its two.
 */
export function pinColourOverride(color: string | undefined, designText: string): string | undefined {
  if (!color) return undefined
  return bakedPinColours(designText).has(color.toLowerCase()) ? undefined : color
}

/**
 * Where a pin's drawing meets its location, following mobile's
 * `PinIconHelper.getIconTranslation`.
 *
 * A drawing with a tip stands on it: every backgrounded pin is a teardrop, and
 * the bare `pin` icon is one too. Everything else — a bare icon, and the
 * directional arrows, which have no tail in either variant — is centred on the
 * point it marks.
 */
export type PinAnchor = 'bottom' | 'center'

export function pinAnchor(type: string, withBackground: boolean): PinAnchor {
  if (withBackground) return type.startsWith('arrow_') ? 'center' : 'bottom'
  return type === 'pin' ? 'bottom' : 'center'
}
